'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { GRASS_CHARACTERISTICS } from '@/data/grass-characteristics'
import { trackEvent } from '@/lib/analytics'
import { useSavedInputs } from '@/hooks/use-saved-inputs'
import type { AnalysisContext, SoilTestInterpretation, SoilTestValues } from '@/lib/soil-test/types'
import { ZIP_INPUT_ATTRS, sanitizeZip } from '@/lib/zip-input'

const STANDARD_GRASS_TYPES = Object.keys(GRASS_CHARACTERISTICS)

export interface SoilTestSubmissionResult {
  soilTestId: string
  claimToken: string | null
  values: SoilTestValues
  interpretation: SoilTestInterpretation
  analysisContext: AnalysisContext
}

interface SoilTestFormProps {
  initialValues?: {
    grassType?: string
    zipCode?: string
    lawnSize?: number
    lawnId?: string
  }
  onSuccess: (result: SoilTestSubmissionResult) => void
}

type Mode = 'upload' | 'manual'

interface PdfError {
  error: string
  message: string
  fallback?: 'manual_entry'
}

// Vercel rejects request bodies over 4.5 MB before the route runs.
const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024

// ============================================================================
// Inline icons (match the design bundle — no new dependencies)
// ============================================================================

function FileIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="14" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
    </svg>
  )
}

function UploadCloud({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 16.58A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" />
      <polyline points="16 16 12 12 8 16" />
      <line x1="12" y1="12" x2="12" y2="21" />
    </svg>
  )
}

function LockIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

function BeakerIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 3h6v5l4.5 9a2 2 0 0 1-1.8 3H6.3a2 2 0 0 1-1.8-3L9 8V3z" />
      <path d="M7 13h10" />
    </svg>
  )
}

function CheckCircle({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  )
}

function ArrowRight({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  )
}

function XIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M6 6l12 12M6 18L18 6" />
    </svg>
  )
}

// ============================================================================
// Form component
// ============================================================================

export function SoilTestForm({ initialValues, onSuccess }: SoilTestFormProps) {
  const { savedInputs, saveInputs } = useSavedInputs()
  const [mode, setMode] = useState<Mode>('upload')
  const [grassType, setGrassType] = useState<string>('')
  const [zipCode, setZipCode] = useState<string>('')
  const [lawnSize, setLawnSize] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<PdfError | null>(null)
  const [manualValues, setManualValues] = useState<Record<string, string>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)
  const editedRef = useRef({ grassType: false, zipCode: false, lawnSize: false })

  useEffect(() => {
    if (initialValues?.grassType && !editedRef.current.grassType && !grassType) {
      setGrassType(initialValues.grassType)
    }
    if (initialValues?.zipCode && !editedRef.current.zipCode && !zipCode) {
      setZipCode(initialValues.zipCode)
    }
    if (initialValues?.lawnSize && !editedRef.current.lawnSize && !lawnSize) {
      setLawnSize(String(initialValues.lawnSize))
    }
  }, [initialValues, grassType, zipCode, lawnSize])

  useEffect(() => {
    if (savedInputs.grassType && !grassType) setGrassType(savedInputs.grassType)
    if (savedInputs.zip && !zipCode) setZipCode(savedInputs.zip)
    if (savedInputs.lawnSize && !lawnSize) setLawnSize(String(savedInputs.lawnSize))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedInputs])

  const handleFile = useCallback((picked: File | null) => {
    if (!picked) {
      setFile(null)
      return
    }
    if (picked.size > MAX_FILE_SIZE_BYTES) {
      setError({ error: 'oversize_file', message: 'File is too large (max 4 MB).' })
      setFile(null)
      return
    }
    if (picked.type && picked.type !== 'application/pdf') {
      setError({ error: 'wrong_type', message: 'Only PDF files are supported.' })
      setFile(null)
      return
    }
    setError(null)
    setFile(picked)
  }, [])

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    const picked = e.dataTransfer.files?.[0] || null
    handleFile(picked)
  }, [handleFile])

  const submitUpload = useCallback(async () => {
    if (!file || !grassType) return
    setSubmitting(true)
    setError(null)
    try {
      const form = new FormData()
      form.set('file', file)
      form.set('grassType', grassType)
      if (zipCode) form.set('zipCode', zipCode)
      if (lawnSize) form.set('lawnSize', lawnSize)
      if (initialValues?.lawnId) form.set('lawnId', initialValues.lawnId)

      const res = await fetch('/api/tools/soil-test/parse', {
        method: 'POST',
        body: form,
      })
      const body = await res.json().catch(() => ({}))

      if (!res.ok) {
        const err: PdfError = {
          error: body.error || 'parse_failed',
          message: body.message || 'Could not process the PDF.',
          fallback: body.fallback,
        }
        setError(err)
        trackEvent('soil_test_parse_failed', { error_code: err.error })
        return
      }

      trackEvent('soil_test_uploaded', {
        lab_name: body.values?.lab,
        parsing_confidence: body.values?.parsingConfidence,
      })
      onSuccess(body as SoilTestSubmissionResult)
    } catch {
      setError({
        error: 'network_error',
        message: 'Network error. Check your connection and try again.',
        fallback: 'manual_entry',
      })
    } finally {
      setSubmitting(false)
    }
  }, [file, grassType, zipCode, lawnSize, initialValues?.lawnId, onSuccess])

  const submitManual = useCallback(async () => {
    if (!grassType) return
    const phNum = Number(manualValues.ph)
    if (!phNum || isNaN(phNum)) {
      setError({ error: 'invalid_input', message: 'pH is required.' })
      return
    }
    setSubmitting(true)
    setError(null)

    const payload: Record<string, unknown> = { grassType, ph: phNum }
    if (zipCode) payload.zipCode = zipCode
    if (lawnSize) payload.lawnSize = Number(lawnSize)
    if (initialValues?.lawnId) payload.lawnId = initialValues.lawnId

    const NUMERIC_KEYS = [
      'organicMatterPct', 'cec',
      'phosphorusPpm', 'potassiumPpm', 'calciumPpm', 'magnesiumPpm', 'sulfurPpm',
      'ironPpm', 'manganesePpm', 'zincPpm', 'boronPpm', 'copperPpm',
    ]
    for (const key of NUMERIC_KEYS) {
      const v = manualValues[key]
      if (v !== undefined && v !== '') {
        const n = Number(v)
        if (!isNaN(n)) payload[key] = n
      }
    }

    try {
      const res = await fetch('/api/tools/soil-test/parse', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const body = await res.json().catch(() => ({}))

      if (!res.ok) {
        setError({
          error: body.error || 'invalid_input',
          message: body.message || body.error || 'Could not save values.',
        })
        return
      }

      trackEvent('soil_test_manual_entry_used', { grass_type: grassType })
      onSuccess(body as SoilTestSubmissionResult)
    } catch {
      setError({ error: 'network_error', message: 'Network error. Try again.' })
    } finally {
      setSubmitting(false)
    }
  }, [grassType, zipCode, lawnSize, manualValues, initialValues?.lawnId, onSuccess])

  const switchToManual = useCallback(() => {
    setMode('manual')
    setError(null)
  }, [])

  const manualPh = Number(manualValues.ph)
  const manualPhValid = manualValues.ph?.trim() !== '' && Number.isFinite(manualPh) && manualPh > 0
  const canSubmit = !submitting && !!grassType && (mode === 'manual' ? manualPhValid : !!file)

  // Labor-perception narration during the async parse (upload mode only —
  // manual entry is fast enough that narration is dishonest). Steps mirror
  // what actually runs in lib/soil-test/parse-pdf.ts and extract-llm.ts.
  const NARRATION_STEPS = [
    'Reading your soil test…',
    'Pulling pH, NPK, and micronutrients…',
    'Matching against your grass targets…',
  ]
  const [narrationStep, setNarrationStep] = useState(0)
  useEffect(() => {
    if (!submitting || mode !== 'upload') {
      setNarrationStep(0)
      return
    }
    const id = setInterval(() => {
      setNarrationStep((step) => Math.min(step + 1, NARRATION_STEPS.length - 1))
    }, 1500)
    return () => clearInterval(id)
  }, [submitting, mode, NARRATION_STEPS.length])

  const submitLabel = submitting
    ? (mode === 'upload' ? 'Analyzing your soil test…' : 'Analyzing values…')
    : (mode === 'upload' ? 'Decode my soil test' : 'Analyze my values')
  const statusLine = submitting && mode === 'upload'
    ? NARRATION_STEPS[narrationStep]
    : null

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-elevation-2 overflow-hidden">
      {/* Tinted header strip: tabs nest inside the card */}
      <div className="bg-gray-50 border-b border-gray-100 px-5 sm:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div role="tablist" className="inline-flex gap-0.5 p-1 bg-gray-100 border border-gray-200 rounded-[10px]">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'upload'}
            onClick={() => { setMode('upload'); setError(null) }}
            className={`px-3.5 py-2 rounded-lg text-[13.5px] font-semibold transition-[background-color,color,box-shadow] ${
              mode === 'upload'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'bg-transparent text-gray-500 hover:text-gray-900'
            }`}
          >
            Upload PDF
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'manual'}
            onClick={() => { setMode('manual'); setError(null) }}
            className={`px-3.5 py-2 rounded-lg text-[13.5px] font-semibold transition-[background-color,color,box-shadow] ${
              mode === 'manual'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'bg-transparent text-gray-500 hover:text-gray-900'
            }`}
          >
            Enter values manually
          </button>
        </div>
        <div className="inline-flex items-center gap-1.5 text-[12.5px] text-gray-500">
          <span className="text-green-700"><LockIcon size={12} /></span>
          Private: file deleted after analysis
        </div>
      </div>

      <div className="p-5 sm:p-6 space-y-5">
        {/* Primary action: drop zone leads. In success state, shows a file chip. */}
        {mode === 'upload' && (
          file ? (
            <FilePickedRow file={file} onReplace={() => fileInputRef.current?.click()} onRemove={() => setFile(null)} />
          ) : (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`rounded-xl border-2 border-dashed transition-colors px-6 py-10 sm:py-12 text-center ${
                dragOver ? 'border-green-600 bg-green-50/50' : 'border-gray-200 bg-gray-50/40'
              }`}
            >
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white border border-green-100 text-green-700 mb-3">
                <UploadCloud size={26} />
              </div>
              <div className="text-[15px] font-semibold text-gray-900 mb-1">Drop your soil test PDF here</div>
              <div className="text-[13px] text-gray-500 mb-4">or click below to choose a file</div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-[13.5px] font-semibold text-gray-900 hover:bg-gray-50"
              >
                <FileIcon size={15} />
                Choose file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] || null)}
              />
              <div className="mt-5 text-[11.5px] text-gray-400">PDF only · 4 MB max</div>
            </div>
          )
        )}

        {mode === 'manual' && <ManualEntryFields values={manualValues} onChange={setManualValues} />}

        {/* Optional context — separated by a dashed rule, marked optional */}
        <div className="pt-5 border-t border-dashed border-gray-200">
          <div className="text-[11px] font-bold text-gray-500 uppercase tracking-[0.08em] mb-3">
            {mode === 'upload' ? 'Sharpen the recommendations' : 'Your lawn (optional but recommended)'}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FieldShell label="Grass type" required>
              <select
                value={grassType}
                onChange={(e) => {
                  editedRef.current.grassType = true
                  setGrassType(e.target.value)
                  if (e.target.value) saveInputs({ grassType: e.target.value })
                }}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-600/30 focus:border-green-600"
              >
                <option value="">Select grass type</option>
                {STANDARD_GRASS_TYPES.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </FieldShell>
            <FieldShell label="ZIP code" optional>
              <input
                {...ZIP_INPUT_ATTRS}
                value={zipCode}
                onChange={(e) => {
                  editedRef.current.zipCode = true
                  const v = sanitizeZip(e.target.value)
                  setZipCode(v)
                  if (/^\d{5}$/.test(v)) saveInputs({ zip: v })
                }}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-600/30 focus:border-green-600"
              />
            </FieldShell>
            <FieldShell label="Lawn size (sqft)" optional>
              <input
                inputMode="numeric"
                value={lawnSize}
                onChange={(e) => {
                  editedRef.current.lawnSize = true
                  const v = e.target.value.replace(/[^\d]/g, '')
                  setLawnSize(v)
                  const n = parseInt(v, 10)
                  if (n > 0) saveInputs({ lawnSize: n })
                }}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-600/30 focus:border-green-600"
              />
            </FieldShell>
          </div>
        </div>

        {/* Trust microcopy split into two scannable rows */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <TrustCell icon={<BeakerIcon size={14} />} title="Any lab works.">
            Logan Labs, Waypoint, Spectrum, A&amp;L Eastern, university extensions. We read values from the PDF text. Or enter them manually.
          </TrustCell>
          <TrustCell icon={<LockIcon size={14} />} title="Private by default.">
            Your PDF is read once to extract values, then discarded. The original file is never stored.
          </TrustCell>
        </div>

        {/* Error envelope */}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-2">
            <div className="text-[13px] font-semibold text-red-800">
              {error.message}
            </div>
            {error.fallback === 'manual_entry' && mode === 'upload' && (
              <button
                type="button"
                onClick={switchToManual}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-red-800 underline hover:no-underline"
              >
                Switch to manual entry
                <ArrowRight size={12} />
              </button>
            )}
          </div>
        )}

        {/* Footer: timing hint or narration + primary CTA. CTA is the loudest thing in the card. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
          <div
            className="text-[13px] text-gray-500"
            aria-live={statusLine ? 'polite' : undefined}
          >
            {statusLine ? (
              <span className="font-medium text-gray-700">{statusLine}</span>
            ) : (
              <>Typical analysis takes <strong className="font-semibold text-gray-900">about 12 seconds</strong>.</>
            )}
          </div>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={mode === 'upload' ? submitUpload : submitManual}
            className="press inline-flex min-h-11 items-center justify-center gap-2.5 rounded-xl bg-action px-5 py-3 text-[15px] font-bold text-action-foreground shadow-elevation-1 hover:bg-action-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {submitLabel}
            {!submitting && <ArrowRight size={16} />}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Subcomponents
// ============================================================================

function FilePickedRow({ file, onReplace, onRemove }: { file: File; onReplace: () => void; onRemove: () => void }) {
  const kb = Math.round(file.size / 1024)
  return (
    <div className="flex items-center gap-3 sm:gap-4 rounded-xl border border-green-100 bg-green-50/60 px-4 py-3.5">
      <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] border border-green-100 bg-white text-green-700">
        <FileIcon size={20} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-gray-900 truncate">{file.name}</div>
        <div className="mt-0.5 inline-flex items-center gap-1.5 text-[12px] text-gray-500">
          <span className="text-green-700"><CheckCircle size={12} /></span>
          {kb} KB · ready to analyze
        </div>
      </div>
      <button
        type="button"
        onClick={onReplace}
        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-gray-700 hover:bg-gray-50"
      >
        Replace
      </button>
      <button
        type="button"
        aria-label="Remove file"
        onClick={onRemove}
        className="inline-flex items-center justify-center p-1.5 text-gray-500 hover:text-gray-700"
      >
        <XIcon size={16} />
      </button>
    </div>
  )
}

function FieldShell({ label, optional, required, children }: { label: string; optional?: boolean; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between mb-1.5">
        <span className="text-[13px] font-semibold text-gray-900">{label}{required && <span className="text-green-700"> *</span>}</span>
        {optional && <span className="text-[12px] font-medium text-gray-400">optional</span>}
      </span>
      {children}
    </label>
  )
}

function TrustCell({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2.5 rounded-[10px] border border-gray-200 bg-gray-50 px-3 py-2.5">
      <span className="mt-0.5 flex-shrink-0 text-green-700">{icon}</span>
      <div className="text-[12.5px] leading-[1.5] text-gray-700">
        <strong className="font-semibold text-gray-900">{title}</strong>{' '}
        {children}
      </div>
    </div>
  )
}

interface ManualEntryFieldsProps {
  values: Record<string, string>
  onChange: (next: Record<string, string>) => void
}

function ManualEntryFields({ values, onChange }: ManualEntryFieldsProps) {
  const setField = (key: string, value: string) => {
    onChange({ ...values, [key]: value })
  }

  const numericField = (key: string, label: string, placeholder: string) => (
    <FieldShell key={key} label={label}>
      <input
        inputMode="decimal"
        required={key === 'ph'}
        placeholder={placeholder}
        value={values[key] ?? ''}
        onChange={(e) => setField(key, e.target.value.replace(/[^\d.]/g, ''))}
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-600/30 focus:border-green-600"
      />
    </FieldShell>
  )

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-gray-500 leading-relaxed">
        Enter the values from your soil test report. Only pH is required; the more you fill in, the better the recommendation.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {numericField('ph', 'pH (required)', '6.5')}
        {numericField('organicMatterPct', 'Organic matter %', '4.0')}
        {numericField('cec', 'CEC (meq/100g)', '12')}
        {numericField('phosphorusPpm', 'P (ppm)', '40')}
        {numericField('potassiumPpm', 'K (ppm)', '175')}
        {numericField('calciumPpm', 'Ca (ppm)', '1200')}
        {numericField('magnesiumPpm', 'Mg (ppm)', '120')}
        {numericField('sulfurPpm', 'S (ppm)', '15')}
        {numericField('ironPpm', 'Fe (ppm)', '75')}
        {numericField('manganesePpm', 'Mn (ppm)', '30')}
        {numericField('zincPpm', 'Zn (ppm)', '3')}
        {numericField('boronPpm', 'B (ppm)', '0.5')}
      </div>
    </div>
  )
}
